import React, { useState } from 'react';
import CodeViewer from '../CodeViewer';

const VulnerabilityScannerDemo = () => {
  const [showCodeViewer, setShowCodeViewer] = useState(false);
  const [target, setTarget] = useState('');
  const [scanResults, setScanResults] = useState(null);

  const handleScan = async () => {
    setScanResults({
      target,
      vulnerabilities: [
        { id: 'CVE-2023-1234', severity: 'high', description: 'SQL Injection vulnerability', cve: 'CVE-2023-1234' },
        { id: 'CVE-2023-5678', severity: 'medium', description: 'XSS vulnerability in form', cve: 'CVE-2023-5678' },
        { id: 'CVE-2023-9012', severity: 'low', description: 'Information disclosure', cve: 'CVE-2023-9012' }
      ],
      timestamp: new Date().toISOString()
    });
  };

  const codeData = {
    code: `import requests
import json
from urllib.parse import urljoin
import threading
from queue import Queue

class VulnerabilityScanner:
    def __init__(self, target_url):
        self.target_url = target_url
        self.vulnerabilities = []
        self.session = requests.Session()
        self.session.headers.update({
            'User-Agent': 'VulnerabilityScanner/1.0'
        })
    
    def scan_sql_injection(self):
        """Scan for SQL injection vulnerabilities"""
        payloads = [
            "' OR '1'='1",
            "1' UNION SELECT NULL--",
            "admin'--",
            "' OR 1=1--"
        ]
        
        vulnerable_params = []
        # Test forms and parameters
        for payload in payloads:
            # Test logic here
            pass
        return vulnerable_params
    
    def scan_xss(self):
        """Scan for XSS vulnerabilities"""
        payloads = [
            "<script>alert('XSS')</script>",
            "<img src=x onerror=alert('XSS')>",
            "javascript:alert('XSS')"
        ]
        
        vulnerable_inputs = []
        # Test XSS vulnerabilities
        return vulnerable_inputs
    
    def scan_authentication(self):
        """Scan for authentication vulnerabilities"""
        issues = []
        
        # Check for weak passwords
        # Check for session management
        # Check for password reset functionality
        
        return issues
    
    def scan_headers(self):
        """Scan security headers"""
        response = self.session.get(self.target_url)
        headers = response.headers
        
        security_headers = {
            'X-Frame-Options': headers.get('X-Frame-Options'),
            'X-Content-Type-Options': headers.get('X-Content-Type-Options'),
            'Strict-Transport-Security': headers.get('Strict-Transport-Security'),
            'Content-Security-Policy': headers.get('Content-Security-Policy')
        }
        
        missing_headers = []
        for header, value in security_headers.items():
            if not value:
                missing_headers.append(header)
        
        return missing_headers
    
    def scan_cve(self, software, version):
        """Check for known CVEs"""
        # Query CVE database
        cve_list = []
        # Implementation to check CVE database
        return cve_list
    
    def generate_report(self):
        """Generate vulnerability report"""
        report = {
            'target': self.target_url,
            'timestamp': datetime.now().isoformat(),
            'vulnerabilities': self.vulnerabilities,
            'summary': {
                'total': len(self.vulnerabilities),
                'critical': len([v for v in self.vulnerabilities if v['severity'] == 'critical']),
                'high': len([v for v in self.vulnerabilities if v['severity'] == 'high']),
                'medium': len([v for v in self.vulnerabilities if v['severity'] == 'medium']),
                'low': len([v for v in self.vulnerabilities if v['severity'] == 'low'])
            }
        }
        return report
    
    def run_scan(self):
        """Run complete vulnerability scan"""
        print(f"Scanning {self.target_url}...")
        
        # Run all scans
        sql_vulns = self.scan_sql_injection()
        xss_vulns = self.scan_xss()
        auth_vulns = self.scan_authentication()
        missing_headers = self.scan_headers()
        
        # Combine results
        self.vulnerabilities = [
            *sql_vulns,
            *xss_vulns,
            *auth_vulns,
            *[{'type': 'missing_header', 'header': h} for h in missing_headers]
        ]
        
        return self.generate_report()

# Usage
scanner = VulnerabilityScanner('https://example.com')
report = scanner.run_scan()
print(json.dumps(report, indent=2))`,
    explanation: `Automated vulnerability scanner for identifying security weaknesses in web applications and infrastructure.

## Scanning Capabilities

**SQL Injection**: Detects SQL injection vulnerabilities in forms and parameters.

**XSS Vulnerabilities**: Identifies cross-site scripting vulnerabilities in user inputs.

**Authentication Issues**: Checks for weak authentication and session management.

**Security Headers**: Verifies presence of security headers like CSP, HSTS.

**CVE Database**: Checks for known vulnerabilities in software versions.

## Technical Implementation

The scanner uses automated testing techniques, payload injection, header analysis, and CVE database queries to identify vulnerabilities and generate comprehensive reports.

## Benefits

- **Automation**: Automated scanning saves time and ensures consistency
- **Comprehensive**: Multiple vulnerability types in single scan
- **Reporting**: Detailed reports with remediation guidance
- **Continuous**: Can be integrated into CI/CD pipelines`,
    technologies: [
      { name: 'Python', description: 'Programming language', tags: ['Scripting', 'Automation'] },
      { name: 'Requests', description: 'HTTP library', tags: ['HTTP', 'Networking'] },
      { name: 'CVE Database', description: 'Vulnerability database', tags: ['Security', 'Database'] },
      { name: 'Nmap', description: 'Network scanner', tags: ['Network', 'Security'] }
    ],
    concepts: [
      { name: 'Vulnerability Assessment', description: 'Identifying security weaknesses', example: 'Automated scanning for vulnerabilities' },
      { name: 'Penetration Testing', description: 'Testing security defenses', example: 'Exploiting vulnerabilities to test defenses' },
      { name: 'CVE Database', description: 'Common Vulnerabilities and Exposures', example: 'Known vulnerability information' },
      { name: 'Security Headers', description: 'HTTP security headers', example: 'CSP, HSTS, X-Frame-Options' }
    ],
    features: [
      'Automated vulnerability scanning',
      'SQL injection detection',
      'XSS vulnerability detection',
      'Security header analysis',
      'CVE database integration',
      'Comprehensive reporting'
    ]
  };

  return (
    <div className="space-y-6">
      <div className="bg-[var(--surface)] p-6 rounded-lg border border-[var(--border)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Vulnerability Scanner</h3>
          <button
            onClick={() => setShowCodeViewer(true)}
            className="px-4 py-2 bg-[var(--accent)] hover:bg-[var(--accent-deep)] text-[var(--text)] rounded-lg transition-colors text-sm"
          >
            View Code
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-[var(--text)] mb-2">Target URL</label>
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="https://example.com"
            className="w-full p-3 bg-[var(--bg)] border border-[var(--border-strong)] rounded-lg text-[var(--text)] placeholder-[var(--muted)] focus:border-[var(--accent)] focus:outline-none"
          />
        </div>

        <button
          onClick={handleScan}
          className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-deep)] text-[var(--text)] rounded-lg font-medium transition-colors mb-4"
        >
          Run Vulnerability Scan
        </button>

        {scanResults && (
          <div className="bg-[var(--bg)] p-4 rounded-lg border border-[var(--border)]">
            <h4 className="text-sm font-medium text-[var(--text)] mb-3">Scan Results</h4>
            <div className="space-y-2">
              {scanResults.vulnerabilities.map((vuln, idx) => (
                <div key={idx} className={`p-3 rounded border ${
                  vuln.severity === 'high' ? 'border-[var(--accent)] bg-[var(--accent-soft)]/20' :
                  vuln.severity === 'medium' ? 'border-[var(--accent)] bg-[var(--accent-soft)]/20' :
                  'border-[var(--border-strong)] bg-[var(--surface)]'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text)] font-medium">{vuln.cve}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      vuln.severity === 'high' ? 'bg-[var(--accent)] text-[var(--text)]' :
                      vuln.severity === 'medium' ? 'bg-[var(--accent)] text-[var(--text)]' :
                      'bg-[var(--border-strong)] text-[var(--text)]'
                    }`}>
                      {vuln.severity.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[var(--text)] text-sm mt-1">{vuln.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CodeViewer
        isOpen={showCodeViewer}
        onClose={() => setShowCodeViewer(false)}
        code={codeData.code}
        language="python"
        title="Vulnerability Scanner"
        explanation={codeData.explanation}
        technologies={codeData.technologies}
        concepts={codeData.concepts}
        features={codeData.features}
      />
    </div>
  );
};

export default VulnerabilityScannerDemo;

